// Ported from motion-primitives (https://motion-primitives.com/docs/animated-background).
// JSX (TS types stripped) version, API-compatible with the upstream component.
//
// Two deliberate deviations from upstream:
//
//   1. `enableHover` mouseLeave falls back to `defaultValue` instead of
//      null — lets callers pass a "current/active" id and have the
//      slider park there whenever no item is hovered.
//
//   2. Dropped AnimatePresence + the opacity initial/animate/exit
//      props. Upstream fades the slider in on enter and out on exit,
//      which works fine in solo-hover mode but creates a visible ghost
//      trail when a `defaultValue` is set: as the slider's `layoutId`
//      animates from A→B, the old motion.div at A is still in the DOM
//      fading out at full position, producing a "smear" alongside the
//      moving new slider. Stripping AnimatePresence makes activeId
//      changes a clean unmount+remount; layoutId still carries the
//      position smoothly between items, and on solo-hover (no
//      defaultValue) the slider simply snaps in/out — same behavior as
//      a standard CSS hover background.
//
//   3. `resetOnItemLeave` lets a parent interaction boundary own the reset.
//      This keeps the slider mounted while the pointer crosses layout gaps
//      between sibling items, then clears it when the parent itself is left.
import { motion, useReducedMotion } from "motion/react"
import { Children, cloneElement, useEffect, useId, useState } from "react"
import { cn } from "@/components/shadcn/lib/utils"

export function AnimatedBackground({
  children,
  defaultValue,
  onValueChange,
  className,
  transition,
  enableHover = false,
  resetOnItemLeave = true,
}) {
  const [activeId, setActiveId] = useState(null)
  const uniqueId = useId()
  const shouldReduceMotion = useReducedMotion()

  const handleSetActiveId = (id) => {
    setActiveId(id)
    onValueChange?.(id)
  }

  useEffect(() => {
    if (defaultValue !== undefined) {
      setActiveId(defaultValue)
    }
  }, [defaultValue])

  // Minor deviation from upstream: key on `data-id` instead of array index,
  // so React reconciliation survives any future child reordering.
  return Children.map(children, (child) => {
    const id = child.props["data-id"]

    // Deviation from upstream: when `enableHover` is on, fall back to
    // `defaultValue` (instead of null) on mouseLeave. This lets callers
    // pass the "currently active" id and have the slider park there
    // whenever no item is hovered — giving us a persistent active
    // indicator that still tracks hover. When no defaultValue is given,
    // behavior matches upstream (slider disappears on mouseLeave).
    const fallback = enableHover ? (defaultValue ?? null) : null

    const interactionProps = enableHover
      ? {
          onMouseEnter: () => handleSetActiveId(id),
          ...(resetOnItemLeave
            ? { onMouseLeave: () => handleSetActiveId(fallback) }
            : {}),
        }
      : {
          onClick: () => handleSetActiveId(id),
        }

    return cloneElement(
      child,
      {
        key: id,
        className: cn("relative inline-flex", child.props.className),
        "data-checked": activeId === id ? "true" : "false",
        ...interactionProps,
      },
      <>
        {activeId === id && (
          <motion.div
            layoutId={`background-${uniqueId}`}
            aria-hidden="true"
            className={cn("pointer-events-none absolute inset-0", className)}
            transition={shouldReduceMotion ? { duration: 0 } : transition}
          />
        )}
        <div className="z-10">{child.props.children}</div>
      </>,
    )
  })
}
